import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"

import loginBg1 from "@/assest/main_bg.png"
import { LoginWallpapeCache } from "@/utils/storage"

const DEFAULT_LOGIN_WALLPAPER = loginBg1

const BackgroundCarousel: React.FC = () => {
  const [thumbRequestUrl, setThumbRequestUrl] = useState<string | null>(
    LoginWallpapeCache.getLoginWallpaperThumbSource() || DEFAULT_LOGIN_WALLPAPER
  )
  const [thumbVisible, setThumbVisible] = useState(false)
  const [originalBgUrl, setOriginalBgUrl] = useState<string | null>(null)
  const [originalVisible, setOriginalVisible] = useState(false)
  const displayedThumbUrlRef = useRef<string | null>(thumbRequestUrl)
  const displayedOriginalUrlRef = useRef<string | null>(null)
  const originalVisibleRef = useRef(false)

  useEffect(() => {
    displayedThumbUrlRef.current = thumbRequestUrl
  }, [thumbRequestUrl])

  useEffect(() => {
    displayedOriginalUrlRef.current = originalBgUrl
  }, [originalBgUrl])

  useEffect(() => {
    originalVisibleRef.current = originalVisible
  }, [originalVisible])

  useEffect(() => {
    let mounted = true
    let loadVersion = 0

    const preloadImage = async (src: string): Promise<void> => {
      const image = new Image()
      image.decoding = "async"
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve()
        image.onerror = () => reject(new Error("image load failed"))
        image.src = src
      })

      if ("decode" in image) {
        await image.decode().catch(() => {})
      }
    }

    const revealOriginal = (originalUrl: string) => {
      if (!mounted) {
        return
      }
      setOriginalBgUrl(originalUrl)
      requestAnimationFrame(() => {
        if (!mounted) {
          return
        }
        setOriginalVisible(true)
        applyDocumentBackground(originalUrl)
      })
    }

    const hydrateWallpaper = async () => {
      const currentLoadVersion = ++loadVersion
      const cachedThumbUrl =
        LoginWallpapeCache.getLoginWallpaperThumbSource() ||
        DEFAULT_LOGIN_WALLPAPER
      const originalUrl =
        LoginWallpapeCache.getLoginWallpaperSource() || DEFAULT_LOGIN_WALLPAPER
      const hasStableOriginal =
        originalVisibleRef.current &&
        displayedOriginalUrlRef.current != null &&
        displayedOriginalUrlRef.current === originalUrl
      const hasSameThumb =
        displayedThumbUrlRef.current === cachedThumbUrl ||
        (displayedThumbUrlRef.current == null && cachedThumbUrl == null)

      if (hasStableOriginal && hasSameThumb) {
        return
      }

      setThumbRequestUrl(cachedThumbUrl)
      setThumbVisible(false)
      setOriginalBgUrl(null)
      setOriginalVisible(false)
      applyDocumentBackground(null)

      if (cachedThumbUrl == null) {
        return
      }

      try {
        await preloadImage(cachedThumbUrl)
        if (!mounted || currentLoadVersion !== loadVersion) {
          return
        }

        setThumbVisible(true)
        applyDocumentBackground(cachedThumbUrl)

        if (originalUrl == null || originalUrl === cachedThumbUrl) {
          return
        }

        await preloadImage(originalUrl)
        if (!mounted || currentLoadVersion !== loadVersion) {
          return
        }

        revealOriginal(originalUrl)
      } catch {
        if (!mounted || currentLoadVersion !== loadVersion) {
          return
        }
        setThumbRequestUrl(cachedThumbUrl)
        setThumbVisible(true)
        setOriginalBgUrl(null)
        setOriginalVisible(false)
        applyDocumentBackground(cachedThumbUrl)
      }
    }

    const handleVisible = () => {
      if (!mounted) {
        return
      }
      void hydrateWallpaper()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return
      }
      handleVisible()
    }

    void hydrateWallpaper()
    window.addEventListener("pageshow", handleVisible)
    window.addEventListener("focus", handleVisible)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      mounted = false
      loadVersion += 1
      window.removeEventListener("pageshow", handleVisible)
      window.removeEventListener("focus", handleVisible)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      document.documentElement.style.backgroundColor = ""
      document.documentElement.style.backgroundImage = ""
      document.documentElement.style.backgroundPosition = ""
      document.documentElement.style.backgroundRepeat = ""
      document.documentElement.style.backgroundSize = ""
      document.body.style.backgroundColor = ""
      document.body.style.backgroundImage = ""
      document.body.style.backgroundPosition = ""
      document.body.style.backgroundRepeat = ""
      document.body.style.backgroundSize = ""
    }
  }, [])

  const handleThumbLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget
    const revealThumb = () => {
      setThumbVisible(true)
      applyDocumentBackground(image.currentSrc || image.src)
    }

    if ("decode" in image) {
      void image.decode().then(revealThumb).catch(revealThumb)
      return
    }

    revealThumb()
  }

  return (
    <>
      {thumbRequestUrl != null ? (
        <BackgroundLayer $visible={thumbVisible} $imageUrl={thumbRequestUrl}>
          <PreloadImage
            src={thumbRequestUrl}
            alt=""
            draggable={false}
            loading="eager"
            fetchPriority="high"
            onLoad={handleThumbLoad}
          />
        </BackgroundLayer>
      ) : null}
      {originalBgUrl != null ? (
        <BackgroundLayer $visible={originalVisible} $imageUrl={originalBgUrl} />
      ) : null}
    </>
  )
}

const BackgroundLayer = styled.div<{ $visible?: boolean; $imageUrl: string }>`
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 0;
  opacity: ${(props) => ((props.$visible ?? true) ? 1 : 0)};
  transition: opacity 0.24s ease;
  pointer-events: none;
  user-select: none;
  background-image: url(${(props) => props.$imageUrl});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  transform: translateZ(0);
  will-change: opacity;
`

const PreloadImage = styled.img`
  width: 0;
  height: 0;
  opacity: 0;
  position: absolute;
  inset: 0;
`

export default BackgroundCarousel

function applyDocumentBackground(bg: string | null) {
  document.documentElement.style.backgroundColor = "#ffffff"
  document.body.style.backgroundColor = "#ffffff"
  document.documentElement.style.backgroundImage = ""
  document.body.style.backgroundImage = ""

  if (bg == null) {
    return
  }

  document.documentElement.style.backgroundImage = `url(${bg})`
  document.body.style.backgroundImage = `url(${bg})`
  document.documentElement.style.backgroundPosition = "center"
  document.documentElement.style.backgroundRepeat = "no-repeat"
  document.documentElement.style.backgroundSize = "cover"
  document.body.style.backgroundPosition = "center"
  document.body.style.backgroundRepeat = "no-repeat"
  document.body.style.backgroundSize = "cover"
}
